import { useRef } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { BackgroundExportStatus } from 'further-types/background-exports';
import { QUERY_STALE_TIME_MS } from 'further-ui/config';
import { downloadLinkAsFile } from 'further-ui/utils';
import trpcApi from 'lib/trpc';
import useUser from 'hooks/ui/useUser';
import { useWhiteLabellingContext } from 'contexts/WhiteLabellingContext';

export const useBackgroundExports = () => {
  const countRef = useRef(0);
  const apiUtils = trpcApi.useUtils();
  const user = useUser();
  const { firm } = useWhiteLabellingContext();

  const { data: backgroundExports } =
    trpcApi.backgroundExports.getBackgroundExportsForFurtherUser.useQuery(
      { firmId: firm?._id },
      {
        placeholderData: keepPreviousData,
        enabled: !!user.isAuthenticated,
        refetchInterval: (query) => {
          const hasPendingExports = query.state.data?.results?.some(
            (backgroundExport) =>
              backgroundExport.status === BackgroundExportStatus.Pending,
          );

          if (!hasPendingExports) {
            countRef.current = 0;
            return false;
          }

          const elapsedTime = countRef.current * 5;
          countRef.current++;

          if (elapsedTime <= 240) {
            return 5000;
          }

          if (elapsedTime <= 1440) {
            return 15000;
          }

          countRef.current = 0;
          return false;
        },
        refetchOnWindowFocus: false,
        staleTime: QUERY_STALE_TIME_MS,
      },
    );

  const downloadBackgroundExport =
    trpcApi.backgroundExports.downloadBackgroundExportForFurtherUser.useMutation(
      {
        onMutate: () => {
          countRef.current = 0; // Reset long poll
        },
        onSuccess: (data) => {
          downloadLinkAsFile(data.downloadLink, data.downloadName);
        },
      },
    );

  const createBackgroundExport =
    trpcApi.backgroundExports.createBackgroundExportForFurtherUser.useMutation({
      onMutate: () => {
        countRef.current = 0; // Reset long poll
      },
      onSuccess: () => {
        apiUtils.backgroundExports.getBackgroundExportsForFurtherUser.invalidate();
      },
    });

  const deleteBackgroundExport =
    trpcApi.backgroundExports.deleteBackgroundExportForFurtherUser.useMutation({
      onSuccess: () => {
        apiUtils.backgroundExports.getBackgroundExportsForFurtherUser.invalidate();
      },
    });

  return {
    backgroundExports,
    downloadBackgroundExport,
    createBackgroundExport,
    deleteBackgroundExport,
    isExporting: createBackgroundExport.isPending,
  };
};
